/**
 * OverviewTab Component
 * Displays cast profile image, name, description, bio, and read-only attributes
 * Moved from Cast.tsx top section to enable full-height tabs
 *
 * LoRA Status Features:
 * - "No LoRA" pill when no LoRA is associated
 * - Training in-progress indicator with status
 * - "Train LoRA" CTA button
 */

import { Loader2, Sparkles, AlertCircle } from "lucide-react";
import type { LucideProps } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CastProfilePicker } from "@/components/cast/CastProfilePicker";
import type { Lora, LoraStatusSlug } from "@/types/loras";

// Cast is characters only. Props and locations will have their own homes.
type CastType = "character";

interface EditableAttribute {
  id: string;
  key: string;
  value: string;
}

interface CastData {
  id: string;
  name: string;
  type: CastType;
  description: string;
  bio: string;
  imageUrl: string | null;
  attributes: Record<string, string>;
}

interface OverviewTabProps {
  cast: CastData;
  typeColor: string;
  typeColorRgb: string;
  TypeIcon: React.FC<LucideProps>;
  attributes: EditableAttribute[];
  /** LoRA associated with this cast (for status indicators) */
  lora?: Lora | null;
  /** Callback to switch tabs (for Train LoRA CTA) */
  onTabChange?: (tab: string) => void;
  /** @deprecated No longer used - attributes are read-only */
  onUpdateAttribute?: (
    id: string,
    field: "key" | "value",
    newValue: string,
  ) => void;
  /** @deprecated No longer used - attributes are read-only */
  onAddAttribute?: () => void;
  /** @deprecated No longer used - attributes are read-only */
  maxAttributes?: number;
}

/**
 * Get display label for LoRA status
 */
function getStatusLabel(status: LoraStatusSlug): string {
  switch (status) {
    case "pending":
      return "Preparing";
    case "training":
      return "Training";
    case "ready":
      return "Ready";
    case "failed":
      return "Failed";
    case "cancelled":
      return "Cancelled";
    case "archived":
      return "Archived";
    default:
      return status;
  }
}

/**
 * Check if status indicates active training (actually running, not just pending)
 */
function isTrainingActive(status: LoraStatusSlug): boolean {
  // "pending" means LoRA created but no training initiated yet - don't show spinner
  // Only show training indicator when actually training
  return status === "training";
}

/**
 * Check if LoRA is in initial state (created but not trained)
 */
function isNotTrained(status: LoraStatusSlug): boolean {
  return status === "pending";
}

export function OverviewTab({
  cast,
  typeColor,
  typeColorRgb,
  TypeIcon,
  attributes,
  lora,
  onTabChange,
}: OverviewTabProps) {
  const hasLora = Boolean(lora);
  const loraStatus = lora?.statusSlug as LoraStatusSlug | undefined;
  const isTraining = loraStatus ? isTrainingActive(loraStatus) : false;
  const isPending = loraStatus ? isNotTrained(loraStatus) : false;
  const isReady = loraStatus === "ready";
  const isFailed = loraStatus === "failed" || loraStatus === "cancelled";

  const handleTrainClick = () => {
    onTabChange?.("train");
  };

  return (
    <div className="cast-overview-tab">
      {/* Left columns container - profile + bio */}
      <div className="cast-left-columns">
        {/* Left column - Profile image and basic info */}
        <div className="flex-shrink-0 w-64">
          {/* Profile image */}
          <div
            className="w-full aspect-square rounded-lg overflow-hidden mb-4"
            style={{
              background: cast.imageUrl
                ? undefined
                : `linear-gradient(135deg, rgba(${typeColorRgb}, 0.19), rgba(${typeColorRgb}, 0.06))`,
            }}
          >
            {cast.imageUrl ? (
              <img
                src={cast.imageUrl}
                alt={cast.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <TypeIcon className="w-20 h-20 opacity-40" color={typeColor} />
              </div>
            )}
          </div>

          {/* Name and type */}
          <h1 className="text-xl font-semibold text-foreground text-left">
            {cast.name}
          </h1>
          {cast.description && (
            <p className="text-sm text-muted-foreground text-left mt-1">
              {cast.description}
            </p>
          )}

          {/* LoRA Status - single line */}
          <div className="mt-3">
            {isReady ? (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <Sparkles className="w-3 h-3 mr-1" />
                LoRA Ready
              </Badge>
            ) : isTraining ? (
              <Badge className="bg-primary/20 text-primary border-primary/30">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Training
              </Badge>
            ) : isFailed && loraStatus ? (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                <AlertCircle className="w-3 h-3 mr-1" />
                {getStatusLabel(loraStatus)}
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="text-muted-foreground border-muted-foreground/30"
              >
                {hasLora ? "Not Trained" : "No LoRA"}
              </Badge>
            )}
          </div>

          {/* Train LoRA CTA - only show when actionable */}
          {onTabChange && !isReady && !isTraining && (
            <Button
              onClick={handleTrainClick}
              variant={isFailed ? "outline" : "default"}
              className="w-full mt-4"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {isFailed ? "Retry Training" : "Train LoRA"}
            </Button>
          )}

          {/* Profile Picker - only when LoRA is trained */}
          {hasLora && lora && !isPending && (
            <div className="mt-4">
              <CastProfilePicker
                loraId={lora.id}
                currentThumbnailAssetId={lora.thumbnailAssetId}
              />
            </div>
          )}
        </div>

        {/* Middle column - Bio */}
        <div className="flex-shrink-0 w-80">
          <h2 className="text-xs text-muted-foreground uppercase tracking-wide mb-3 text-left">
            Bio
          </h2>
          <p className="text-sm text-foreground leading-relaxed text-left">
            {cast.bio}
          </p>
        </div>
      </div>

      {/* Spacer */}
      <div className="cast-spacer" />

      {/* Right column - Attributes (read-only) */}
      <div className="cast-attributes">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs text-muted-foreground uppercase tracking-wide text-left">
            Attributes
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {attributes.map((attr) => (
            <div key={attr.id}>
              <span className="block text-xs text-muted-foreground uppercase tracking-wide">
                {attr.key}
              </span>
              <span className="block text-sm font-medium text-foreground">
                {attr.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
